import { useEffect, useMemo, useRef, useState, ChangeEvent } from "react";
import { NavDropdown } from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import TheEnvironmentTitle from "./TheEnvironmentTitle";
import { useNavigate } from "react-router-dom";
import { getSorter } from "system/libraries/utilities";
import { useDispatch, useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { Icon } from "@fluentui/react/lib/Icon";
import { useResponsive, usePageNavigation, useApi, useConfiguration } from "system/hooks";
import { getInitials } from "@fluentui/react";
import LogoutIcon from "@mui/icons-material/Logout";
import { policyEditorActions } from "system/store/slices/policy-editor-slice";
import { Button, FormControlLabel, Switch,  Tooltip, Dialog, DialogTitle, DialogContent, List, ListItemButton, ListItemText } from "@mui/material";
import NotificationDrawer from "components/modules/manage-alert/components/alertNotification/NotificationDrawer";
import { RootState } from "system/store";
import { getAccountEmail } from "system/utils/getAccountEmail";
import { MSAL_SCOPES } from "components/Intune/IntuneScopes";
import { IntuneToggle } from "components/Intune/IntuneToggle";


//COMPONENT: displays the logo, contact me link and admin page's icon
const TheHeader = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const [userMenuVisible, setUserMenuVisible] = useState(false);
  const msalContext = useMsal();
  const navigate = useNavigate();
  const { navigateToHomePage } = usePageNavigation();
  const { toggleMobileSidebarVisibility, isMobileSidebarVisible } = useResponsive();
  const { hasAdminPermission, isIntuneEnabled } = useConfiguration();
  const [showDrawer, setShowDrawer] = useState(false);
  const unreadCount = useSelector((state: RootState) => state.alerts.unreadCount);

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && buttonRef.current && !menuRef.current.contains(event.target as Node) && event.target !== buttonRef.current) {
        setUserMenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { tenants, currentTenantId } = useSelector((state: IRootState) => state.config);
  const sortedTenants = useMemo(() => {
    if (!tenants?.length) {
      return [];
    }

    return [...tenants].sort(getSorter("siteName"));
  }, [tenants]);

  const onTenantChange = (tenant: number | string) => {
    dispatch(policyEditorActions.resetPolicyEditorState());
    navigate(`/${tenant}`);
  };

  const currentTenantName = tenants?.find((x) => x.siteId === currentTenantId)?.siteName ?? "Company";

  //For admin screen navigate
  const handleAdminClick = (tenant: number | null) => {
    if (tenant === null) {
      navigate(`/${tenant}`);

      return;
    } else {
      navigate(`/${tenant}/admin`);
    }
  };

  return (
    <nav className="navbar">
      <div className="nav-container">
        <div className="logos">
          <div className="logo" onClick={navigateToHomePage}>
            <img alt="logo-name" src="/img/aiden-logo-400-1.png" />
            <TheEnvironmentTitle />
          </div>
        </div>
        {isIntuneEnabled && (
          <div className="intune-toggle">
            <IntuneToggle />
          </div>
        )}
       
        <div className="contact">
          {msalContext?.accounts?.length > 0 && (
            <>
              <Icon
                iconName={isMobileSidebarVisible ? "Cancel" : "GlobalNavButton"}
                onClick={() => toggleMobileSidebarVisibility()}
                className="menu"
              />

              {(tenants?.length ?? 0) > 0 && (
                <NavDropdown title={currentTenantName} style={{ zIndex: "auto", width: "33.33%", textAlign: "center" }}>
                  {sortedTenants.map((tenant) => (
                    <NavDropdown.Item
                      className={tenant.siteId === currentTenantId ? "active" : ""}
                      onClick={(e) => {
                        onTenantChange(tenant.siteId);
                        e.preventDefault();
                      }}
                      key={tenant.siteId}
                      href="/#"
                    >
                      {tenant.siteName}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              )}

              <div className="account">
                <span
                  className="clickable"
                  onClick={() => handleAdminClick(currentTenantId)}
                  style={{ display: hasAdminPermission ? "block" : "none" }}
                >
                  <i className="bi bi-gear" style={{ fontSize: "16px" }}></i>
                </span>
                <div className="user" onClick={() => setUserMenuVisible(!userMenuVisible)} ref={buttonRef}>
                  {getInitials(msalContext.accounts[0]?.name, false, false)}
                </div>
                <Tooltip
                  title={unreadCount ? `You have ${unreadCount} unread alert${unreadCount !== 1 ? "s" : ""}` : "No unread messages"}
                  placement="bottom-end"
                >
                  <div className="bell-icon" onClick={() => setShowDrawer(true)}>
                    <img alt="bell-icon" src="/img/bell-count.svg" />
                    {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                  </div>
                </Tooltip>
                <NotificationDrawer visible={showDrawer} onClose={() => setShowDrawer(false)} />
              </div>
            </>
          )}
        </div>

        {userMenuVisible && (
          <div className="userMenu" ref={menuRef}>
            <div className="userMenu__header">
              <img alt="logo-icon" src="/img/aiden-logo-400-1.png" />
              <div onClick={() => api.auth.logout()}>
                <LogoutIcon fontSize="small" /> Sign out
              </div>
            </div>
            <div className="userMenu__body">
              <div className="user user-menu" onClick={() => setUserMenuVisible(!userMenuVisible)}>
                {getInitials(msalContext.accounts[0]?.name, false, false)}
              </div>
              <div className="userMenu__body--info">
                <span className="userMenu__body--name">{msalContext.accounts[0]?.name}</span>
                <span className="userMenu__body--email">{getAccountEmail(msalContext.accounts[0])}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default TheHeader;
